<template>
    <div class="mall-v2-page">
        <a-spin :spinning="loading">
            <div class="mall-v2-header">
                <div>
                    <div class="mall-v2-title">商城装修 V2</div>
                    <div class="mall-v2-subtitle">整套管理首页、开卡页、个人中心和购物车草稿，并统一发布</div>
                </div>
                <div class="mall-v2-actions">
                    <a-switch :checked="useDecorateV2" checked-children="V2" un-checked-children="V1" @change="toggleV2" />
                    <a-button icon="reload" @click="loadAll">刷新</a-button>
                    <a-button type="primary" icon="cloud-upload" @click="publishAll">整套发布</a-button>
                </div>
            </div>

            <div class="mall-v2-layout">
                <div class="mall-v2-sidebar">
                    <a-tabs default-active-key="page" size="small">
                        <a-tab-pane key="page" tab="页面">
                            <a-list :data-source="pageList" size="small">
                                <a-list-item slot="renderItem" slot-scope="item" :class="{active: currentPage && currentPage.pageType === item.pageType}" @click="selectPage(item.pageType)">
                                    <div>
                                        <div class="mall-v2-list-title">{{ item.pageName }}</div>
                                        <div class="mall-v2-list-desc">{{ item.pageType }}</div>
                                    </div>
                                </a-list-item>
                            </a-list>
                        </a-tab-pane>
                        <a-tab-pane key="template" tab="模板">
                            <a-list :data-source="templateList" size="small">
                                <a-list-item slot="renderItem" slot-scope="item">
                                    <div class="template-row">
                                        <div>
                                            <div class="mall-v2-list-title">{{ item.templateName }}</div>
                                            <div class="mall-v2-list-desc">{{ item.templateCode }}</div>
                                        </div>
                                        <a-button size="small" @click="applyTemplate(item)">应用</a-button>
                                    </div>
                                </a-list-item>
                            </a-list>
                        </a-tab-pane>
                    </a-tabs>
                </div>

                <div class="mall-v2-preview">
                    <div class="phone-frame">
                        <div class="phone-title">{{ currentPage ? currentPage.pageName : '页面预览' }}</div>
                        <mall-v2-preview :content="parsedContent" />
                    </div>
                </div>

                <div class="mall-v2-editor">
                    <div class="editor-title-row">
                        <div>
                            <div class="editor-title">页面结构</div>
                            <div class="editor-subtitle">支持快速插入常用控件，页面仍可直接编辑 JSON</div>
                        </div>
                        <a-tag v-if="draftComponents.length" color="blue">{{ draftComponents.length }} 个控件</a-tag>
                    </div>

                    <div class="component-list">
                        <a-empty v-if="!draftComponents.length" description="当前页面暂无控件，先从控件库插入一个模块" />
                        <template v-else>
                            <div
                                v-for="(item, index) in draftComponents"
                                :key="item.id || index"
                                class="component-card"
                                :class="{ active: selectedComponentIndex === index }"
                                @click="selectedComponentIndex = index"
                            >
                                <div class="component-card-main">
                                    <div class="component-card-title">{{ getComponentLabel(item) }}</div>
                                    <div class="component-card-desc">{{ getComponentSummary(item) }}</div>
                                </div>
                                <div class="component-card-actions">
                                    <a-button size="small" @click.stop="moveComponent(index, -1)">上移</a-button>
                                    <a-button size="small" @click.stop="moveComponent(index, 1)">下移</a-button>
                                    <a-button size="small" @click.stop="duplicateComponent(index)">复制</a-button>
                                    <a-button size="small" type="danger" @click.stop="removeComponent(index)">删除</a-button>
                                </div>
                            </div>
                        </template>
                    </div>

                    <div class="editor-title-row editor-gap">
                        <div>
                            <div class="editor-title">控件库</div>
                            <div class="editor-subtitle">市面上常用的商城装修控件，点击即可插入到当前页面</div>
                        </div>
                    </div>

                    <div class="control-grid">
                        <div v-for="preset in controlPresets" :key="preset.type" class="control-card">
                            <div class="control-card-title">{{ preset.label }}</div>
                            <div class="control-card-desc">{{ preset.description }}</div>
                            <a-button size="small" type="primary" @click="insertPreset(preset)">插入</a-button>
                        </div>
                    </div>

                    <div class="editor-title-row editor-gap">
                        <div>
                            <div class="editor-title">草稿 JSON</div>
                            <div class="editor-subtitle">如果需要批量调整结构，可直接编辑 JSON</div>
                        </div>
                    </div>

                    <a-textarea v-model="draftContent" :auto-size="{ minRows: 18, maxRows: 28 }" />
                    <div class="editor-actions">
                        <a-button @click="formatDraft">格式化</a-button>
                        <a-button type="primary" @click="saveDraft">保存草稿</a-button>
                    </div>
                </div>
            </div>
        </a-spin>
    </div>
</template>

<script>
import MallV2Preview from './components/MallV2Preview'
import {
    applyMallTemplateV2,
    enableMallDecorateV2,
    getMallPageV2,
    initMallDecorateV2,
    listMallPageV2,
    listMallTemplateV2,
    publishMallPageV2,
    saveMallPageV2Draft
} from '@/api/mallDecorateV2'
import { V2_CONTROL_PRESETS, cloneV2Component } from './schema/mallV2Controls'

export default {
    name: 'MallDecorateV2',
    components: { MallV2Preview },
    data() {
        return {
            loading: false,
            storeShopId: null,
            useDecorateV2: false,
            pageList: [],
            templateList: [],
            currentPage: null,
            draftContent: '{}',
            selectedComponentIndex: -1,
            controlPresets: V2_CONTROL_PRESETS
        }
    },
    computed: {
        parsedContent() {
            if (!this.draftContent) {
                return null
            }
            try {
                return JSON.parse(this.draftContent)
            } catch (e) {
                return null
            }
        },
        draftComponents() {
            const content = this.parsedContent
            return content && Array.isArray(content.components) ? content.components : []
        }
    },
    methods: {
        loadAll() {
            if (!this.storeShopId) {
                this.$message.warning('缺少店铺ID')
                return
            }
            this.loading = true
            initMallDecorateV2(this.storeShopId)
                .then(() => Promise.all([listMallPageV2(this.storeShopId), listMallTemplateV2()]))
                .then(([pageRes, templateRes]) => {
                    if (pageRes.success) {
                        this.pageList = pageRes.result || []
                        if (!this.currentPage && this.pageList.length > 0) {
                            this.selectPage(this.pageList[0].pageType)
                        }
                    }
                    if (templateRes.success) {
                        this.templateList = templateRes.result || []
                    }
                })
                .finally(() => {
                    this.loading = false
                })
        },
        selectPage(pageType) {
            this.loading = true
            getMallPageV2(this.storeShopId, pageType)
                .then(res => {
                    if (res.success) {
                        this.currentPage = res.result
                        this.draftContent = this.currentPage && this.currentPage.draftContent ? this.currentPage.draftContent : '{}'
                        this.selectedComponentIndex = -1
                    }
                })
                .finally(() => {
                    this.loading = false
                })
        },
        parseDraft() {
            try {
                const model = this.draftContent ? JSON.parse(this.draftContent) : {}
                if (!Array.isArray(model.components)) {
                    model.components = []
                }
                return model
            } catch (e) {
                this.$message.error('JSON 格式不正确，请先修正后再操作控件')
                return null
            }
        },
        saveModel(model, keepSelection = true) {
            this.draftContent = JSON.stringify(model, null, 2)
            if (!keepSelection) {
                this.selectedComponentIndex = -1
            }
        },
        updateDraft(mutator) {
            const model = this.parseDraft()
            if (!model) {
                return false
            }
            mutator(model)
            this.saveModel(model)
            return true
        },
        insertPreset(preset) {
            const component = preset && typeof preset.create === 'function' ? preset.create() : null
            if (!component) {
                return
            }
            this.updateDraft(model => {
                model.components.push(component)
                this.selectedComponentIndex = model.components.length - 1
            })
        },
        duplicateComponent(index) {
            this.updateDraft(model => {
                const source = model.components[index]
                if (!source) {
                    return
                }
                const copy = cloneV2Component(source)
                copy.id = `${copy.type || 'component'}_${Date.now().toString(36)}`
                model.components.splice(index + 1, 0, copy)
                this.selectedComponentIndex = index + 1
            })
        },
        moveComponent(index, delta) {
            this.updateDraft(model => {
                const target = index + delta
                if (target < 0 || target >= model.components.length) {
                    return
                }
                const list = model.components
                const current = list[index]
                list.splice(index, 1)
                list.splice(target, 0, current)
                this.selectedComponentIndex = target
            })
        },
        removeComponent(index) {
            this.updateDraft(model => {
                model.components.splice(index, 1)
                if (this.selectedComponentIndex === index) {
                    this.selectedComponentIndex = -1
                } else if (this.selectedComponentIndex > index) {
                    this.selectedComponentIndex -= 1
                }
            })
        },
        getComponentLabel(item) {
            const preset = this.controlPresets.find(presetItem => presetItem.type === item.type)
            if (preset) {
                return preset.label
            }
            return item.type || '未命名控件'
        },
        getComponentSummary(item) {
            if (!item) {
                return ''
            }
            const parts = [item.type]
            if (item.id) {
                parts.push(item.id)
            }
            if (item.visible === false) {
                parts.push('隐藏')
            }
            return parts.join(' · ')
        },
        formatDraft() {
            try {
                this.draftContent = JSON.stringify(JSON.parse(this.draftContent), null, 2)
            } catch (e) {
                this.$message.error('JSON 格式不正确')
            }
        },
        saveDraft() {
            if (!this.currentPage) {
                return
            }
            try {
                JSON.parse(this.draftContent)
            } catch (e) {
                this.$message.error('JSON 格式不正确，不能保存')
                return
            }
            this.loading = true
            saveMallPageV2Draft({
                storeShopId: this.storeShopId,
                pageType: this.currentPage.pageType,
                draftContent: this.draftContent
            })
                .then(res => {
                    if (res.success) {
                        this.$message.success('已保存草稿')
                        this.loadAll()
                    }
                })
                .finally(() => {
                    this.loading = false
                })
        },
        publishAll() {
            this.$confirm({
                title: '确认整套发布？',
                content: '将同时发布首页、开卡页、个人中心和购物车的当前草稿。',
                onOk: () => {
                    this.loading = true
                    return publishMallPageV2(this.storeShopId)
                        .then(res => {
                            if (res.success) {
                                this.$message.success('发布成功')
                            }
                        })
                        .finally(() => {
                            this.loading = false
                        })
                }
            })
        },
        applyTemplate(template) {
            this.$confirm({
                title: `应用模板：${template.templateName}`,
                content: '模板会覆盖四类页面草稿，但不会影响已发布版本。',
                onOk: () => {
                    this.loading = true
                    return applyMallTemplateV2({
                        storeShopId: this.storeShopId,
                        templateId: template.id,
                        templateCode: template.templateCode
                    })
                        .then(res => {
                            if (res.success) {
                                this.$message.success('模板已应用')
                                this.currentPage = null
                                this.loadAll()
                            }
                        })
                        .finally(() => {
                            this.loading = false
                        })
                }
            })
        },
        toggleV2(checked) {
            this.loading = true
            enableMallDecorateV2({
                storeShopId: this.storeShopId,
                useDecorateV2: checked
            })
                .then(res => {
                    if (res.success) {
                        this.useDecorateV2 = checked
                        this.$message.success('开关已更新')
                    }
                })
                .finally(() => {
                    this.loading = false
                })
        }
    },
    created() {
        this.storeShopId = this.$route.query.storeShopId || this.$route.query.id
        this.useDecorateV2 = this.$route.query.useDecorateV2 === 'true'
        this.loadAll()
    }
}
</script>

<style scoped>
.mall-v2-page {
    padding: 12px;
}

.mall-v2-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    padding: 12px 16px;
    background: #ffffff;
}

.mall-v2-title {
    font-size: 18px;
    font-weight: 600;
    color: #222222;
}

.mall-v2-subtitle {
    margin-top: 4px;
    color: #888888;
}

.mall-v2-actions {
    display: flex;
    align-items: center;
    gap: 8px;
}

.mall-v2-layout {
    display: grid;
    grid-template-columns: 240px 390px minmax(360px, 1fr);
    gap: 12px;
}

.mall-v2-sidebar,
.mall-v2-preview,
.mall-v2-editor {
    min-height: 640px;
    padding: 12px;
    background: #ffffff;
}

.mall-v2-list-title {
    color: #222222;
    font-weight: 500;
}

.mall-v2-list-desc,
.editor-subtitle {
    color: #999999;
    font-size: 12px;
}

.active {
    background: #f0f5ff;
}

.template-row {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.phone-frame {
    width: 360px;
    min-height: 640px;
    margin: 0 auto;
    border: 1px solid #eeeeee;
    background: #f7f7f7;
}

.phone-title {
    height: 44px;
    line-height: 44px;
    text-align: center;
    background: #ffffff;
    font-weight: 600;
}

.editor-title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
}

.editor-gap {
    margin-top: 16px;
}

.editor-title {
    font-weight: 600;
    color: #222222;
}

.component-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.component-card,
.control-card {
    padding: 10px 12px;
    border: 1px solid #eeeeee;
    border-radius: 8px;
    background: #fafafa;
}

.component-card {
    cursor: pointer;
}

.component-card.active {
    border-color: #1890ff;
    background: #eef6ff;
}

.component-card-main {
    margin-bottom: 8px;
}

.component-card-title,
.control-card-title {
    color: #222222;
    font-weight: 600;
}

.component-card-desc,
.control-card-desc {
    margin-top: 4px;
    color: #888888;
    font-size: 12px;
    line-height: 1.5;
}

.component-card-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.control-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
}

.editor-actions {
    margin-top: 12px;
    display: flex;
    justify-content: flex-end;
    gap: 8px;
}
</style>
